/**
 * AREA TICKS - who an area attack catches, answered by hand.
 *
 * With positions counting, the map answers "who is inside the shape, in
 * what order, and set off by whom". With positions off there is no shape to
 * be inside, so the table says it: the attacker ticks who is caught, and the
 * GM confirms. This is that answer as data, and the few pure operations on
 * it. It carries exactly what the map's answer carries - ids, an order (an
 * arc's leaps are its order), and for a secondary template who set it off -
 * so everything after it (the automatic hit, the test to step clear, damage
 * lost per leap, nobody struck twice) runs unchanged.
 *
 * Pure, zero React dependency.
 */

export interface SpawnedTick {
  /** Who the secondary template catches. */
  id: string;
  /** Which of the primary's victims set it off. */
  from: string;
}

export interface AreaTicks {
  /** Who the template catches, IN ORDER: for an arc, the order the chain reaches them. */
  caught: string[];
  /** Who the secondary template catches, when the weapon has one. */
  spawned: SpawnedTick[];
}

export const NO_TICKS: AreaTicks = { caught: [], spawned: [] };

/** A fresh answer with the aimed target ticked - where every aimed shot starts. */
export const ticksFor = (aimedId: string | null): AreaTicks => ({ caught: aimedId ? [aimedId] : [], spawned: [] });

/**
 * Tick or untick someone as caught. Ticking appends, which is what orders
 * an arc. Unticking also drops anything they set off, and their own entry
 * in the secondary list: nobody is struck twice by one shot.
 */
export function toggleCaught(ticks: AreaTicks, id: string): AreaTicks {
  if (!ticks.caught.includes(id)) {
    return { caught: [...ticks.caught, id], spawned: ticks.spawned.filter((s) => s.id !== id) };
  }
  return { caught: ticks.caught.filter((c) => c !== id), spawned: ticks.spawned.filter((s) => s.from !== id) };
}

/**
 * Tick or untick someone as caught by the secondary template. They are set
 * off by `from`, which must be one of the caught; someone already caught by
 * the primary cannot also be caught by the secondary.
 */
export function toggleSpawned(ticks: AreaTicks, id: string, from: string): AreaTicks {
  if (ticks.spawned.some((s) => s.id === id)) return { ...ticks, spawned: ticks.spawned.filter((s) => s.id !== id) };
  if (ticks.caught.includes(id) || !ticks.caught.includes(from)) return ticks;
  return { ...ticks, spawned: [...ticks.spawned, { id, from }] };
}

/** Change who set off an already ticked secondary victim. */
export function setSpawner(ticks: AreaTicks, id: string, from: string): AreaTicks {
  if (!ticks.caught.includes(from)) return ticks;
  return { ...ticks, spawned: ticks.spawned.map((s) => (s.id === id ? { id, from } : s)) };
}

/**
 * Each caught id's leap. An arc's first victim is leap 0 (the one aimed at)
 * and each later one is one leap further; every other shape strikes all its
 * victims at once, at leap 0.
 */
export function leapsOf(ticks: AreaTicks, isArc: boolean): Map<string, number> {
  return new Map(ticks.caught.map((id, index) => [id, isArc ? index : 0]));
}

/**
 * The secondary template's victims for THIS resolution: those set off by a
 * victim who actually spawned (`spawnerIds`), and who were not struck by
 * the primary after all (`struckIds`).
 */
export function spawnedFor(
  ticks: AreaTicks,
  spawnerIds: readonly string[],
  struckIds: readonly string[],
): SpawnedTick[] {
  return ticks.spawned.filter((s) => spawnerIds.includes(s.from) && !struckIds.includes(s.id));
}

const isId = (value: unknown): value is string => typeof value === 'string' && value !== '';

/**
 * An answer that crossed the wire, validated: ids only, each once, and a
 * secondary victim only when whoever set it off is among the caught. What
 * it cannot read is dropped; an unreadable answer is nobody.
 */
export function readTicks(raw: unknown): AreaTicks {
  const stored = (raw ?? {}) as { caught?: unknown; spawned?: unknown };
  const caught = [...new Set(Array.isArray(stored.caught) ? stored.caught.filter(isId) : [])];
  const seen = new Set(caught);
  const spawned: SpawnedTick[] = [];
  for (const entry of Array.isArray(stored.spawned) ? stored.spawned : []) {
    const { id, from } = (entry ?? {}) as { id?: unknown; from?: unknown };
    if (!isId(id) || !isId(from) || seen.has(id) || !caught.includes(from)) continue;
    seen.add(id);
    spawned.push({ id, from });
  }
  return { caught, spawned };
}
